import {
  getToolCallIdFromPart,
  isPendingHITLPart,
} from './chat-message-part-accessors';
import type {
  ChatRenderItem,
  RenderablePart,
  SubAgentExecutionGroup,
} from './chat-message-list-types';

function renderablePartHasToolCallId({
  item,
  toolCallId,
}: {
  item: RenderablePart;
  toolCallId: string;
}): boolean {
  return getToolCallIdFromPart(item.processedPart) === toolCallId;
}

function subAgentGroupHasToolCallId({
  group,
  toolCallId,
}: {
  group: SubAgentExecutionGroup;
  toolCallId: string;
}): boolean {
  if (renderablePartHasToolCallId({ item: group.parent, toolCallId })) {
    return true;
  }

  return (
    group.continuationParents.some((item) =>
      renderablePartHasToolCallId({ item, toolCallId }),
    ) ||
    group.children.some((item) =>
      renderablePartHasToolCallId({ item, toolCallId }),
    )
  );
}

export function findChatRenderItemIndexForToolCallId({
  items,
  toolCallId,
}: {
  items: ChatRenderItem[];
  toolCallId: string;
}): number {
  return items.findIndex((item) => {
    if (item.kind === 'sub-agent-group') {
      return subAgentGroupHasToolCallId({ group: item.group, toolCallId });
    }

    if (item.kind === 'part') {
      return renderablePartHasToolCallId({ item: item.item, toolCallId });
    }

    return false;
  });
}

function renderItemHasPendingHITL(item: ChatRenderItem): boolean {
  if (item.kind === 'part') {
    return isPendingHITLPart(item.item.processedPart);
  }

  if (item.kind === 'consolidated-group') {
    return item.items.some((entry) => isPendingHITLPart(entry.processedPart));
  }

  if (item.kind === 'sub-agent-group') {
    return [
      item.group.parent,
      ...item.group.continuationParents,
      ...item.group.children,
    ].some((entry) => isPendingHITLPart(entry.processedPart));
  }

  return false;
}

export function findLatestTurnPendingHITLRenderItemIndex(
  items: ChatRenderItem[],
): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];

    if (item.kind === 'user-message') {
      return -1;
    }

    if (renderItemHasPendingHITL(item)) {
      return index;
    }
  }

  return -1;
}
